import type { LabelGateDiscoveryReason } from '../dto/counter-label/label-gate-discovery-reason.enum';
import type { PrepackagedPrintGateCode } from '../label/prepackaged-print-gate-codes';

/**
 * Tassonomia unica dei rilievi allergeni. Ogni superficie decide la severita'
 * di ciascun codice nella propria `AllergenSurfacePolicy`; i codici "wire"
 * (gate del cartellino, errori del preimballato) restano quelli di oggi e si
 * ricavano dalle tabelle qui sotto, cosi' frontend e i18n non cambiano.
 */
export const ALLERGEN_ISSUE_CODES = [
  'UNLINKED_ROW',
  'NEVER_DECLARED',
  'UNDECLARED_COMPOSITION',
  'MISSING_BREAKDOWN',
  'KEYLESS_CONTAINS',
  'MAY_CONTAIN_NAME_MISSING',
  'MISSING_ADDITIVE_NAME',
  'COMPOSITION_LIMIT',
  'COMPOSITION_OUT_OF_SCOPE',
  'RECIPE_GRAPH_LIMIT',
  'LEGACY_FROZEN_FIELD',
  'ANNEX_II_EXEMPTION_APPLIED',
  'ANNEX_II_EXEMPTION_NOT_APPLIED',
  'SULPHITE_CONTENT_UNKNOWN',
] as const;
export type AllergenIssueCode = (typeof ALLERGEN_ISSUE_CODES)[number];

export type IssueSeverity = 'BLOCK' | 'WARN' | 'INFO' | 'IGNORE';

/**
 * Rilievi dopo i quali l'elenco allergeni non e' verificabile: il cartello
 * deve dire "da verificare", mai "nessuno" (art. 44 Reg. UE 1169/2011,
 * art. 19 D.Lgs 231/2017). Una superficie che li declassa a `IGNORE` (il
 * lotto congela gli allergeni noti) non li conta.
 */
export const MAKES_ALLERGENS_UNVERIFIED: ReadonlySet<AllergenIssueCode> = new Set([
  'UNLINKED_ROW',
  'NEVER_DECLARED',
  'COMPOSITION_LIMIT',
  'COMPOSITION_OUT_OF_SCOPE',
  'RECIPE_GRAPH_LIMIT',
]);

/** Motivi del gate di conferma del cartellino (409): i codici wire non cambiano. */
export const LABEL_GATE_REASON_BY_ISSUE: Readonly<
  Partial<Record<AllergenIssueCode, LabelGateDiscoveryReason>>
> = {
  UNLINKED_ROW: 'UNLINKED_RECIPE_INGREDIENT',
  NEVER_DECLARED: 'UNDECLARED_INGREDIENT_ALLERGENS',
  UNDECLARED_COMPOSITION: 'UNDECLARED_COMPOSITION',
  MISSING_BREAKDOWN: 'MISSING_BREAKDOWN',
  KEYLESS_CONTAINS: 'MISSING_ALLERGEN_SUBSTANCE',
  MAY_CONTAIN_NAME_MISSING: 'MISSING_ALLERGEN_SUBSTANCE',
  MISSING_ADDITIVE_NAME: 'MISSING_ADDITIVE_NAME_OR_E_NUMBER',
  COMPOSITION_LIMIT: 'INGREDIENT_COMPOSITION_TOO_DEEP',
  COMPOSITION_OUT_OF_SCOPE: 'INGREDIENT_COMPOSITION_TOO_DEEP',
};

/** `RECIPE_GRAPH_LIMIT` porta il tipo di limite in `detail`: due motivi wire distinti. */
export function labelGateReasonForRecipeGraphLimit(
  limitStatus: 'TOO_DEEP' | 'TOO_LARGE',
): LabelGateDiscoveryReason {
  return limitStatus === 'TOO_DEEP' ? 'RECIPE_GRAPH_TOO_DEEP' : 'RECIPE_GRAPH_TOO_LARGE';
}

/**
 * Codici di errore/avviso del compositore del preimballato. Non tutti sono nel
 * catalogo `PREPACKAGED_PRINT_GATE_CODES` (quello elenca solo i codici con una
 * spiegazione dedicata): `MISSING_BREAKDOWN` e `UNKNOWN_ALLERGEN_SUBSTANCE`
 * viaggiano da prima come codici del compositore.
 */
export type PrepackagedAllergenWireCode =
  | Extract<
      PrepackagedPrintGateCode,
      | 'MISSING_ALLERGEN_CATALOG_KEY'
      | 'MISSING_ADDITIVE_NAME_OR_E_NUMBER'
      | 'INGREDIENT_COMPOSITION_TOO_DEEP'
      | 'FROZEN_COMPONENT_SOURCE_MISSING'
      | 'FROZEN_COMPONENT_ALLERGEN_UNKNOWN'
      | 'ALLERGEN_EXEMPT_ANNEX_II'
      | 'ALLERGEN_EXEMPTION_NOT_APPLIED_USER_DECLARED'
      | 'SULPHITES_CONTENT_UNKNOWN'
    >
  | 'MISSING_BREAKDOWN'
  | 'UNKNOWN_ALLERGEN_SUBSTANCE';

/**
 * `NEVER_DECLARED`, `UNDECLARED_COMPOSITION` e `MISSING_BREAKDOWN` sono per il
 * preimballato lo stesso difetto («nessuna scomposizione»). I due codici
 * `FROZEN_COMPONENT_*` si distinguono dal `detail` dell'issue (`SOURCE_MISSING`
 * o `ALLERGEN_UNKNOWN`), vedi `frozenFieldWireCode`.
 */
export const PREPACKAGED_CODE_BY_ISSUE: Readonly<
  Partial<Record<AllergenIssueCode, PrepackagedAllergenWireCode>>
> = {
  NEVER_DECLARED: 'MISSING_BREAKDOWN',
  UNDECLARED_COMPOSITION: 'MISSING_BREAKDOWN',
  MISSING_BREAKDOWN: 'MISSING_BREAKDOWN',
  KEYLESS_CONTAINS: 'MISSING_ALLERGEN_CATALOG_KEY',
  MISSING_ADDITIVE_NAME: 'MISSING_ADDITIVE_NAME_OR_E_NUMBER',
  MAY_CONTAIN_NAME_MISSING: 'UNKNOWN_ALLERGEN_SUBSTANCE',
  COMPOSITION_LIMIT: 'INGREDIENT_COMPOSITION_TOO_DEEP',
  COMPOSITION_OUT_OF_SCOPE: 'INGREDIENT_COMPOSITION_TOO_DEEP',
  ANNEX_II_EXEMPTION_APPLIED: 'ALLERGEN_EXEMPT_ANNEX_II',
  ANNEX_II_EXEMPTION_NOT_APPLIED: 'ALLERGEN_EXEMPTION_NOT_APPLIED_USER_DECLARED',
  SULPHITE_CONTENT_UNKNOWN: 'SULPHITES_CONTENT_UNKNOWN',
};

export type LegacyFrozenFieldKind = 'COMPONENT_SOURCE_MISSING' | 'COMPONENT_ALLERGEN_UNKNOWN';

export function frozenFieldWireCode(
  kind: LegacyFrozenFieldKind,
): 'FROZEN_COMPONENT_SOURCE_MISSING' | 'FROZEN_COMPONENT_ALLERGEN_UNKNOWN' {
  return kind === 'COMPONENT_SOURCE_MISSING'
    ? 'FROZEN_COMPONENT_SOURCE_MISSING'
    : 'FROZEN_COMPONENT_ALLERGEN_UNKNOWN';
}
